// Live embroidery preview for the homepage feature banner [17].
// - Color swatches swap the product photo (the variant's blank image).
// - Designs rotate on a timer, paused while typing, while the page is hidden and
//   under reduced motion.
// - Typing initials (A–Z, up to three) shows them all: typing on any design that is not a
//   monogram jumps to the monogram design (or a centered monogram when the section has none).
// Configuration (colors, designs, positions as % of the photo) is rendered by
// sections/home-duffle.liquid from the product and the section's design blocks.

export function cleanInitials(value) {
  return String(value || '').replace(/[^A-Za-z]/g, '').toUpperCase().slice(0, 3);
}

/** Light bag colors get the light-thread color so letters keep their contrast. */
export function threadFor(hex, { lightThread, darkThread }) {
  const match = /^#?([0-9a-f]{6})$/i.exec(String(hex || '').trim());
  if (!match) return darkThread;
  const [r, g, b] = [0, 2, 4].map(index => parseInt(match[1].slice(index, index + 2), 16));
  return (r * 299 + g * 587 + b * 114) / 1000 > 150 ? lightThread : darkThread;
}

/** Elements to draw for one design: icons and letters with their positions. */
export function designItems(design, letters) {
  const text = cleanInitials(letters);
  return (design?.items || []).flatMap(item => {
    if (item.type === 'icon') return item.src ? [{ kind: 'icon', src: item.src, x: item.x, y: item.y, w: item.w }] : [];
    if (item.type === 'initials') return text ? [{ kind: 'text', text, x: item.x, y: item.y, size: item.sizes?.[text.length] }] : [];
    if (item.type === 'initial') return text ? [{ kind: 'text', text: text[0], x: item.x, y: item.y, size: item.size }] : [];
    return [];
  });
}

/** Shown while typing when the section has no monogram design: every letter, centered. */
export const TYPING_MONOGRAM = { kind: 'mono', items: [{ type: 'initials', x: 50, y: 46, sizes: [0, 12, 10.5, 8.5] }] };

/** The design to show while typing: the current one if it is a monogram, else the first monogram (-1: none). */
export function typingDesignIndex(designs, index) {
  if (designs?.[index]?.kind === 'mono') return index;
  return (designs || []).findIndex(design => design.kind === 'mono');
}

let HomeDuffle;
if (typeof HTMLElement !== 'undefined') HomeDuffle = class extends HTMLElement {
  connectedCallback() {
    const raw = this.querySelector('[data-home-duffle-config]');
    this.preview = this.querySelector('[data-home-duffle-preview]');
    this.photo = this.querySelector('[data-home-duffle-photo]');
    this.input = this.querySelector('[data-home-duffle-initials]');
    this.colorName = this.querySelector('[data-home-duffle-color-name]');
    if (!raw || !this.preview || !this.input) return;
    try { this.config = JSON.parse(raw.textContent); } catch { return; }
    this.controller = new AbortController();
    const options = { signal: this.controller.signal };
    this.color = this.config.colors[0] || { hex: '' };
    this.design = 0;
    this.initials = cleanInitials(this.config.initials);
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    this.addEventListener('click', event => {
      const swatch = event.target.closest('[data-home-duffle-color]');
      if (swatch) this.setColor(swatch.dataset.homeDuffleColor);
    }, options);
    // The first keystroke after focusing replaces the sample initials.
    let fresh = false;
    this.input.addEventListener('focus', () => { fresh = true; }, options);
    this.input.addEventListener('beforeinput', event => {
      if (fresh && event.inputType.startsWith('insert')) this.input.value = '';
      fresh = false;
    }, options);
    this.input.addEventListener('input', () => {
      const clean = cleanInitials(this.input.value);
      if (clean !== this.input.value) this.input.value = clean;
      this.initials = clean;
      const monogram = typingDesignIndex(this.config.designs, this.design);
      if (monogram >= 0) this.design = monogram;
      this.typing = monogram < 0 ? TYPING_MONOGRAM : null;
      this.render();
    }, options);
    document.addEventListener('visibilitychange', () => this.syncTimer(), options);
    this.reducedMotion.addEventListener('change', () => this.syncTimer(), options);
    this.render();
    this.syncTimer();
  }

  disconnectedCallback() {
    this.controller?.abort();
    window.clearInterval(this.timer);
    this.timer = null;
  }

  syncTimer() {
    window.clearInterval(this.timer);
    this.timer = null;
    if (this.config.designs.length < 2 || this.reducedMotion.matches || document.hidden) return;
    this.timer = window.setInterval(() => {
      if (document.activeElement === this.input) return;
      this.typing = null;
      this.design = (this.design + 1) % this.config.designs.length;
      this.render();
    }, Math.max(2000, Number(this.config.rotateMs) || 4000));
  }

  current() { return this.typing || this.config.designs[this.design]; }

  setColor(name) {
    const next = this.config.colors.find(color => color.name === name);
    if (!next || next === this.color) return;
    this.color = next;
    if (this.photo) {
      this.photo.removeAttribute('srcset');
      this.photo.src = next.image;
      this.photo.alt = next.alt || '';
      this.photo.classList.remove('is-swapping');
      void this.photo.offsetWidth;
      this.photo.classList.add('is-swapping');
    }
    this.querySelectorAll('[data-home-duffle-color]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.homeDuffleColor === name)));
    if (this.colorName) this.colorName.textContent = name;
    this.render();
  }

  render() {
    this.preview.querySelectorAll('.embroidery-preview__icon, .embroidery-preview__text').forEach(element => element.remove());
    const letters = this.initials || this.config.initials;
    const thread = threadFor(this.color.hex, this.config);
    for (const item of designItems(this.current(), letters)) {
      if (item.kind === 'icon') {
        const image = document.createElement('img');
        image.className = 'embroidery-preview__icon';
        image.src = item.src;
        image.alt = '';
        image.style.cssText = `--x: ${Number(item.x)}; --y: ${Number(item.y)}; --w: ${Number(item.w)}`;
        this.preview.append(image);
      } else {
        const span = document.createElement('span');
        span.className = 'embroidery-preview__text embroidery-preview__text--block';
        span.dataset.clarityMask = 'True';
        span.textContent = item.text;
        span.style.cssText = `--x: ${Number(item.x)}; --y: ${Number(item.y)}; --size: ${Number(item.size) || 10}`;
        span.style.setProperty('--thread', thread);
        this.preview.append(span);
      }
    }
  }
};

if (HomeDuffle && !customElements.get('home-duffle')) customElements.define('home-duffle', HomeDuffle);
